import { useEffect, useState } from 'react';

import { isEmpty } from 'lodash-es';

import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useStoreContext } from 'state/store';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { IMenuObjectWithDaypart, getAvailabilityStatus, itemIsAvailable } from 'utils/availability';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';
import { IWithVendorConfig } from 'utils/vendor-config';

import { IncentiveEvaluationMap } from './types';
import {
  buildOutOfDayPartEvaluationResult,
  buildVendorEvaluationResult,
} from './utils/incentives-availability';

export const useRewardsEvaluation = () => {
  const { engineRewardsMap, sanityRewardsMap } = useLoyaltyRewardsList();
  const { prices, store } = useStoreContext();
  const { vendor } = usePosVendor();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const [rewardsEvaluationMap, setRewardsEvaluationMap] = useState<IncentiveEvaluationMap>({});

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  useEffect(() => {
    if (
      isEmpty(sanityRewardsMap) ||
      isEmpty(engineRewardsMap) ||
      (!isUnifiedMenuEnabled && !prices) ||
      (isUnifiedMenuEnabled && (!indexedMenu || !posServiceMode)) ||
      (isUnifiedMenuEnabled && isUnifiedMenuLoading)
    ) {
      return;
    }

    const buildEvaluationResultsMap = () => {
      const evaluationsMap = Object.values(sanityRewardsMap ?? {}).reduce(
        (evaluationMap, sanityReward) => {
          const loyaltyEngineId = sanityReward?.loyaltyEngineId ?? '';
          const engineReward = engineRewardsMap[loyaltyEngineId];

          if (!engineReward || engineReward?.locked) {
            evaluationMap[loyaltyEngineId] = [];
            return evaluationMap;
          }

          let isRewardAvailable;
          if (isUnifiedMenuEnabled) {
            const rewardEntry = indexedMenu.find(sanityReward._id);
            isRewardAvailable =
              !!rewardEntry &&
              isEntryAvailable(
                rewardEntry,
                posServiceMode,
                activeDayParts.map(d => d.key)
              );
          } else {
            isRewardAvailable = itemIsAvailable(sanityReward as IWithVendorConfig, vendor, prices);
          }
          if (!isRewardAvailable) {
            evaluationMap[loyaltyEngineId] = [buildVendorEvaluationResult()];
            return evaluationMap;
          }

          const incentive = sanityReward?.incentives?.[0] ?? undefined;
          let status: ItemAvailabilityStatus | null = null;
          if (isUnifiedMenuEnabled) {
            const menuEntry = indexedMenu.find(incentive?._id ?? '');
            status = getOneMenuAvailability(
              menuEntry,
              activeDayParts,
              posServiceMode,
              Boolean(store.hasBurgersForBreakfast)
            );
          } else {
            status = getAvailabilityStatus({
              data: incentive as IMenuObjectWithDaypart,
              activeDayParts,
              prices,
              vendor,
              isExtra: false,
              storeHasBurgersForBreakfast: Boolean(store.hasBurgersForBreakfast),
            });
          }

          let evaluationsResult = null;
          switch (status) {
            case ItemAvailabilityStatus.AVAILABLE:
            case ItemAvailabilityStatus.STORE_NOT_SELECTED:
              break;
            case ItemAvailabilityStatus.OUT_OF_DAYPART:
              evaluationsResult = buildOutOfDayPartEvaluationResult(sanityReward, dayParts);
              break;
            default:
              evaluationsResult = buildVendorEvaluationResult();
          }

          if (evaluationsResult) {
            evaluationMap[loyaltyEngineId] = [evaluationsResult];
          }

          return evaluationMap;
        },
        {} as IncentiveEvaluationMap
      );

      return evaluationsMap;
    };

    setRewardsEvaluationMap(buildEvaluationResultsMap());
  }, [
    activeDayParts,
    dayParts,
    engineRewardsMap,
    indexedMenu,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    posServiceMode,
    prices,
    sanityRewardsMap,
    store.hasBurgersForBreakfast,
    vendor,
  ]);

  return {
    rewardsEvaluationMap,
    engineRewardsMap,
    sanityRewardsMap,
  };
};
